import {AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {take} from 'rxjs/operators';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ProjectService} from '@webmate-web-core/shared/services/project.service';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {MarketplaceItemId, MarketplaceTagKey, ProjectId} from '@webmate-web-core/shared/types/ids';
import {ProjectInfo} from '@webmate-web-core/shared/types/projectInfo';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {
    MarketplaceAcquireAction,
    MarketplaceAction,
    MarketplaceGrantTarget,
    MarketplaceItem,
    MarketplacePricingFeatureId,
    MarketplaceProvideFeedbackAction,
    MarketplaceRequest,
    MarketplaceRequestServiceAction,
    MarketplaceSignalInterestAction,
    MarketplaceTagDefinition
} from '@webmate-web-core/shared/types/marketplace';
import {MarketplaceService} from '@webmate-web-core/shared/services/marketplace.service';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import {TOOL_CONTAINER_DATA} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.component';
import {ModalToolContainerService, WebmateToolConfig} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {ApiSourcedPaginationRequest} from '@webmate-web-core/shared/api/apibase.api';

type MarketplaceDialog = 'request' | 'consultation' | 'feedback' | 'requests';

interface MarketplaceProjectTargetOption {
    value: string;
    label: string;
}

const ALL_PROJECTS_TARGET = '__all_projects__';
const MARKETPLACE_REQUEST_PAGE_SIZE = 50;

export class MarketplaceToolConfig implements WebmateToolConfig {
    readonly toolName = 'Marketplace';
    readonly hideHeader = true;
    readonly notDismissible = true;
    readonly wideContainer = true;

    constructor(public readonly preferredProjectId?: ProjectId) {
    }
}

@UntilDestroy()
@Component({
    selector: 'wm-marketplace',
    templateUrl: './marketplace.component.html',
    styleUrls: ['./marketplace.component.less']
})
export class MarketplaceComponent implements AfterViewInit, OnDestroy, OnInit {

    @ViewChild('marketplaceContent') private marketplaceContent?: ElementRef<HTMLElement>;
    @ViewChild('marketplaceInitialFocus') private marketplaceInitialFocus?: ElementRef<HTMLButtonElement>;

    items: MarketplaceItem[] = [];
    featuredItem?: MarketplaceItem;
    recommendedItems: MarketplaceItem[] = [];
    serviceItems: MarketplaceItem[] = [];
    roadmapItems: MarketplaceItem[] = [];
    featuredTags: MarketplaceTagDefinition[] = [];
    isMarketplaceLoading = true;
    marketplaceLoadError = '';
    pricingVisible = false;

    activeTag?: MarketplaceTagKey;
    searchQuery = '';
    selectedItem?: MarketplaceItem;

    projects: ProjectInfo[] = [];
    projectTargetOptions: MarketplaceProjectTargetOption[] = [];
    selectedProjectTargetValues: string[] = [];
    projectsLoading = true;
    projectsLoadError = false;
    isCustomerAdmin = false;
    requestAction?: MarketplaceAcquireAction;
    requestNote = '';
    requestStep = 1;
    requests: MarketplaceRequest[] = [];
    isRequestsLoading = true;
    isMoreRequestsLoading = false;
    hasMoreRequests = false;
    requestsLoadError = '';
    requestsLoadMoreError = '';

    dialog?: MarketplaceDialog;
    dialogItem?: MarketplaceItem;
    dialogAction?: MarketplaceRequestServiceAction | MarketplaceProvideFeedbackAction;
    consultationMessage = '';
    feedbackMessage = '';
    interestedItemIds = new Set<MarketplaceItemId>();
    pendingInterestItemIds = new Set<MarketplaceItemId>();
    interactionSubmissionPending = false;
    interactionError = '';

    private requestsLoadGeneration = 0;
    private nextRequestsPage?: () => ApiSourcedPaginationRequest<MarketplaceRequest>;
    private dialogTrigger?: HTMLElement;
    private marketplaceTrigger?: HTMLElement;

    constructor(@Inject(TOOL_CONTAINER_DATA) private data: MarketplaceToolConfig,
                private marketplaceService: MarketplaceService,
                private featureService: FeatureService,
                private projectService: ProjectService,
                private userService: UserService,
                private toolService: ModalToolContainerService,
                private notificationService: WmNotificationService,
                @Inject(DOCUMENT) private document: Document) {
        if (this.document.activeElement instanceof HTMLElement &&
            this.document.activeElement !== this.document.body) {
            this.marketplaceTrigger = this.document.activeElement;
        }
    }

    ngOnInit(): void {
        this.loadMarketplaceItems();
        this.featureService.getFeature(MarketplacePricingFeatureId)
            .pipe(take(1), untilDestroyed(this))
            .subscribe(feature => this.pricingVisible = feature?.isActive === true);
        this.userService.getCurrentUserData()
            .pipe(take(1), untilDestroyed(this))
            .subscribe({
                next: user => {
                    this.isCustomerAdmin = user.administrativePermissions.length > 0;
                    this.loadMarketplaceRequests();
                    this.loadProjects();
                },
                error: () => {
                    this.projectsLoading = false;
                    this.projectsLoadError = true;
                    this.requestsLoadGeneration++;
                    this.nextRequestsPage = undefined;
                    this.requests = [];
                    this.isRequestsLoading = false;
                    this.isMoreRequestsLoading = false;
                    this.hasMoreRequests = false;
                    this.requestsLoadError = 'We could not determine which Marketplace requests you may view.';
                    this.requestsLoadMoreError = '';
                }
            });
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.marketplaceInitialFocus?.nativeElement.focus());
    }

    ngOnDestroy(): void {
        if (this.marketplaceTrigger?.isConnected) {
            this.marketplaceTrigger.focus();
        }
    }

    retryMarketplaceLoad(): void {
        this.loadMarketplaceItems();
    }

    retryMarketplaceRequests(): void {
        this.loadMarketplaceRequests();
    }

    loadMoreMarketplaceRequests(): void {
        if (!this.hasMoreRequests || this.isMoreRequestsLoading) {
            return;
        }
        this.loadMarketplaceRequests(true);
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscape(event: KeyboardEvent): void {
        if (event.defaultPrevented) {
            return;
        }
        event.preventDefault();
        if (this.dialog) {
            this.closeDialog();
        } else {
            this.closeMarketplace();
        }
    }

    get isResultsMode(): boolean {
        return !!this.activeTag || this.searchQuery.trim().length > 0;
    }

    get activeTagDefinition(): MarketplaceTagDefinition | undefined {
        return this.featuredTags.find(tag => tag.key === this.activeTag);
    }

    get filteredItems(): MarketplaceItem[] {
        const query = this.searchQuery.trim().toLowerCase();
        return this.items.filter(item => {
            const tagMatches = !this.activeTag || item.tagKeys.includes(this.activeTag);
            const searchableText = [
                item.title,
                item.teaser,
                item.valueProp,
                item.provider,
                item.kindLabel,
                ...item.tags.map(tag => tag.label)
            ].join(' ').toLowerCase();
            return tagMatches && (query.length === 0 || searchableText.includes(query));
        });
    }

    get requestTarget(): MarketplaceGrantTarget | undefined {
        if (this.isCustomerAdmin && this.selectedProjectTargetValues.includes(ALL_PROJECTS_TARGET)) {
            return {type: 'allProjects'};
        }
        const visibleProjectIds = new Set(this.projects.map(project => project.id));
        const projectIds = this.selectedProjectTargetValues.filter(projectId => visibleProjectIds.has(projectId));
        return projectIds.length > 0 ? {type: 'projects', projectIds} : undefined;
    }

    get requestTargetLabel(): string {
        if (this.requestTarget?.type === 'allProjects') {
            return `All projects (${this.projects.length})`;
        }
        const selectedIds = new Set(this.requestTarget?.type === 'projects' ? this.requestTarget.projectIds : []);
        const selectedProjects = this.projects.filter(project => selectedIds.has(project.id));
        if (selectedProjects.length <= 3) {
            return selectedProjects.map(project => project.name).join(', ');
        }
        return `${selectedProjects.length} projects (${selectedProjects.slice(0, 3).map(project => project.name).join(', ')}, …)`;
    }

    get canContinueRequest(): boolean {
        return !this.interactionSubmissionPending && (this.requestStep !== 1 || !!this.requestTarget);
    }

    get dialogTitle(): string {
        if (this.dialog === 'requests') {
            return 'Purchase requests';
        }
        return this.dialogItem?.title || 'Marketplace';
    }

    selectTag(tagKey?: MarketplaceTagKey): void {
        this.activeTag = tagKey;
    }

    showDetails(item: MarketplaceItem): void {
        this.selectedItem = item;
        this.scrollContentToTop();
    }

    showHome(): void {
        this.selectedItem = undefined;
        this.scrollContentToTop();
    }

    handleCardAction(item: MarketplaceItem): void {
        this.handleAction(item, item.primaryAction);
    }

    handleAction(item: MarketplaceItem, action: MarketplaceAction): void {
        switch (action.kind) {
            case 'acquire':
                this.openRequest(item, action);
                break;
            case 'requestService':
                this.openConsultation(item, action);
                break;
            case 'signalInterest':
                this.signalInterest(item, action);
                break;
            case 'provideFeedback':
                this.openFeedback(item, action);
                break;
        }
    }

    actionLabel(item: MarketplaceItem, action: MarketplaceAction): string {
        if (action.kind === 'signalInterest' && this.isInterestSubmissionPending(item)) {
            return 'Saving…';
        }
        if (action.kind === 'signalInterest' && this.isInterested(item)) {
            return '✓ Interest recorded';
        }
        return action.label;
    }

    openRequest(item: MarketplaceItem, action: MarketplaceAcquireAction): void {
        this.dialogItem = item;
        this.requestAction = action;
        this.requestStep = 1;
        this.requestNote = '';
        this.interactionError = '';
        this.selectPreferredProjectTargets();
        this.showDialog('request');
    }

    onProjectTargetSelectionChange(selection: string[]): void {
        const distinctSelection = [...new Set(selection || [])];
        this.selectedProjectTargetValues = distinctSelection.includes(ALL_PROJECTS_TARGET)
            ? [ALL_PROJECTS_TARGET]
            : distinctSelection;
    }

    openConsultation(item: MarketplaceItem, action: MarketplaceRequestServiceAction): void {
        this.dialogItem = item;
        this.dialogAction = action;
        this.consultationMessage = '';
        this.interactionError = '';
        this.showDialog('consultation');
    }

    openFeedback(item: MarketplaceItem, action: MarketplaceProvideFeedbackAction): void {
        this.dialogItem = item;
        this.dialogAction = action;
        this.feedbackMessage = '';
        this.interactionError = '';
        this.showDialog('feedback');
    }

    openRequests(): void {
        this.dialogItem = undefined;
        this.dialogAction = undefined;
        this.requestAction = undefined;
        this.interactionError = '';
        this.showDialog('requests');
        this.loadMarketplaceRequests();
    }

    closeDialog(): void {
        if (this.interactionSubmissionPending) {
            return;
        }
        this.dialog = undefined;
        this.dialogItem = undefined;
        this.dialogAction = undefined;
        this.requestAction = undefined;
        this.requestStep = 1;
        this.interactionError = '';
    }

    onDialogVisibilityChange(visible: boolean): void {
        if (!visible) {
            this.closeDialog();
        }
    }

    restoreDialogTriggerFocus(): void {
        const trigger = this.dialogTrigger;
        this.dialogTrigger = undefined;
        if (trigger?.isConnected) {
            trigger.focus();
        }
    }

    previousRequestStep(): void {
        this.requestStep = Math.max(1, this.requestStep - 1);
    }

    continueRequest(): void {
        if (!this.canContinueRequest) {
            return;
        }
        if (this.requestStep < 3) {
            this.requestStep++;
            return;
        }
        this.submitRequest();
    }

    submitRequest(): void {
        const target = this.requestTarget;
        if (this.interactionSubmissionPending || !this.dialogItem || !this.requestAction || !target) {
            return;
        }
        const item = this.dialogItem;
        const action = this.requestAction;
        const note = this.requestNote.trim();
        this.beginDialogSubmission();
        this.marketplaceService.recordInteraction$({
            marketplaceItemId: item.id,
            itemTitleSnapshot: item.title,
            interactionId: action.id,
            payload: {
                type: 'acquisitionRequest',
                catalogOfferingRefs: [...action.catalogOfferingRefs],
                target,
                ...(note ? {note} : {})
            }
        }).pipe(take(1), untilDestroyed(this)).subscribe({
            next: () => {
                this.interactionSubmissionPending = false;
                this.loadMarketplaceRequests();
                this.requestStep = 4;
                this.notificationService.toastNotification('Request recorded for purchasing review.', 'Marketplace');
            },
            error: () => this.failDialogSubmission('We could not record this request. Please try again.')
        });
    }

    submitConsultation(): void {
        if (this.interactionSubmissionPending || !this.dialogItem || !this.dialogAction || this.dialogAction.kind !== 'requestService') {
            return;
        }
        const item = this.dialogItem;
        const action = this.dialogAction;
        this.beginDialogSubmission();
        this.marketplaceService.recordInteraction$({
            marketplaceItemId: item.id,
            itemTitleSnapshot: item.title,
            interactionId: action.id,
            payload: {
                type: 'serviceInquiry',
                ...(action.serviceRef ? {serviceRef: action.serviceRef} : {}),
                message: this.consultationMessage.trim()
            }
        }).pipe(take(1), untilDestroyed(this)).subscribe({
            next: () => {
                this.interactionSubmissionPending = false;
                this.closeDialog();
                this.notificationService.toastNotification('Consultation request recorded.', 'Marketplace');
            },
            error: () => this.failDialogSubmission('We could not record this consultation request. Please try again.')
        });
    }

    submitFeedback(): void {
        if (this.interactionSubmissionPending || !this.dialogItem || !this.dialogAction || this.dialogAction.kind !== 'provideFeedback' || !this.feedbackMessage.trim()) {
            return;
        }
        const item = this.dialogItem;
        const action = this.dialogAction;
        this.beginDialogSubmission();
        this.marketplaceService.recordInteraction$({
            marketplaceItemId: item.id,
            itemTitleSnapshot: item.title,
            interactionId: action.id,
            payload: {
                type: 'feedback',
                message: this.feedbackMessage.trim()
            }
        }).pipe(take(1), untilDestroyed(this)).subscribe({
            next: () => {
                this.interactionSubmissionPending = false;
                this.closeDialog();
                this.notificationService.toastNotification('Thanks. Your feedback was recorded.', 'Marketplace');
            },
            error: () => this.failDialogSubmission('We could not record your feedback. Please try again.')
        });
    }

    signalInterest(item: MarketplaceItem, action: MarketplaceSignalInterestAction): void {
        if (this.isInterestSubmissionPending(item) || this.isInterested(item)) {
            return;
        }
        this.pendingInterestItemIds.add(item.id);
        this.marketplaceService.recordInteraction$({
            marketplaceItemId: item.id,
            itemTitleSnapshot: item.title,
            interactionId: action.id,
            payload: {
                type: 'interestChanged',
                interested: true
            }
        }).pipe(take(1), untilDestroyed(this)).subscribe({
            next: () => {
                this.pendingInterestItemIds.delete(item.id);
                this.interestedItemIds.add(item.id);
                this.notificationService.toastNotification('Interest recorded. Thank you.', 'Marketplace');
            },
            error: () => {
                this.pendingInterestItemIds.delete(item.id);
                this.notificationService.toastErrorNotification('We could not record your interest. Please try again.');
            }
        });
    }

    isInterested(item: MarketplaceItem): boolean {
        return this.interestedItemIds.has(item.id);
    }

    isInterestSubmissionPending(item: MarketplaceItem): boolean {
        return this.pendingInterestItemIds.has(item.id);
    }

    interestCount(item: MarketplaceItem): number {
        return (item.interestCount || 0) + (this.isInterested(item) ? 1 : 0);
    }

    relatedItems(item: MarketplaceItem): MarketplaceItem[] {
        return item.relatedItemIds
            .map(itemId => this.items.find(candidate => candidate.id === itemId))
            .filter((candidate): candidate is MarketplaceItem => !!candidate);
    }

    requestTargetDisplayLabel(target: MarketplaceGrantTarget): string {
        switch (target.type) {
            case 'user':
                return `User ${target.userId}`;
            case 'project':
                return this.projectDisplayName(target.projectId);
            case 'projects':
                return this.projectSelectionDisplayLabel(target.projectIds);
            case 'allProjects':
                return this.projects.length > 0 ? `All projects (${this.projects.length})` : 'All projects';
            case 'organization':
                return `Organization ${target.organizationId}`;
        }
    }

    closeMarketplace(): void {
        if (this.interactionSubmissionPending) {
            return;
        }
        this.closeDialog();
        this.selectedItem = undefined;
        // Closing in the next task keeps the parent portal container from changing during this component's event check.
        setTimeout(() => this.toolService.closeLastTool());
    }

    loadProjects(): void {
        this.projectsLoading = true;
        this.projectsLoadError = false;
        this.projects = [];
        this.projectTargetOptions = [];

        const projects$ = this.isCustomerAdmin
            ? this.projectService.getProjectInfosForCustomer()
            : this.projectService.getProjectInfosForCurrentUser();

        projects$.pipe(take(1), untilDestroyed(this)).subscribe({
            next: projects => {
                this.projects = [...projects].sort((left, right) => left.name.localeCompare(right.name));
                this.projectTargetOptions = [
                    ...(this.isCustomerAdmin ? [{value: ALL_PROJECTS_TARGET, label: `All projects (${this.projects.length})`}] : []),
                    ...this.projects.map(project => ({value: project.id, label: project.name}))
                ];
                this.projectsLoading = false;
                this.selectPreferredProjectTargets();
            },
            error: () => {
                this.projectsLoading = false;
                this.projectsLoadError = true;
            }
        });
    }

    private selectPreferredProjectTargets(): void {
        const preferredProject = this.data.preferredProjectId && this.projects.some(project => project.id === this.data.preferredProjectId)
            ? this.data.preferredProjectId
            : undefined;
        this.selectedProjectTargetValues = preferredProject ? [preferredProject] : [];
    }

    private loadMarketplaceItems(): void {
        this.isMarketplaceLoading = true;
        this.marketplaceLoadError = '';
        this.marketplaceService.getPublishedItems$().pipe(take(1), untilDestroyed(this)).subscribe({
            next: items => {
                this.items = items;
                this.featuredItem = this.marketplaceService.getPlacedItems(items, 'home.hero')[0];
                this.recommendedItems = this.marketplaceService.getPlacedItems(items, 'home.recommended');
                this.serviceItems = this.marketplaceService.getPlacedItems(items, 'home.services');
                this.roadmapItems = this.marketplaceService.getPlacedItems(items, 'home.roadmap');
                this.featuredTags = this.marketplaceService.getFeaturedTags(items);
                this.isMarketplaceLoading = false;
            },
            error: () => {
                this.isMarketplaceLoading = false;
                this.marketplaceLoadError = 'We could not load the Marketplace. Please try again.';
            }
        });
    }

    private loadMarketplaceRequests(append = false): void {
        const loadGeneration = ++this.requestsLoadGeneration;
        if (append) {
            this.isMoreRequestsLoading = true;
            this.requestsLoadMoreError = '';
        } else {
            this.nextRequestsPage = undefined;
            this.requests = [];
            this.isRequestsLoading = true;
            this.isMoreRequestsLoading = false;
            this.hasMoreRequests = false;
            this.requestsLoadError = '';
            this.requestsLoadMoreError = '';
        }
        const request = append
            ? this.nextRequestsPage?.()
            : (this.isCustomerAdmin
                ? this.marketplaceService.getRequestsForCurrentCustomer$(MARKETPLACE_REQUEST_PAGE_SIZE)
                : this.marketplaceService.getRequestsForCurrentUser$(MARKETPLACE_REQUEST_PAGE_SIZE));
        if (!request) {
            this.isMoreRequestsLoading = false;
            this.hasMoreRequests = false;
            return;
        }
        request.fetch().pipe(take(1), untilDestroyed(this)).subscribe({
            next: response => {
                if (loadGeneration !== this.requestsLoadGeneration) {
                    return;
                }
                const page = response.result || [];
                if (append) {
                    const loadedRequestIds = new Set(this.requests.map(request => request.id));
                    this.requests = [
                        ...this.requests,
                        ...page.filter(request => !loadedRequestIds.has(request.id))
                    ];
                } else {
                    this.requests = page;
                }
                this.isRequestsLoading = false;
                this.isMoreRequestsLoading = false;
                this.hasMoreRequests = response.hasNextPage();
                this.nextRequestsPage = this.hasMoreRequests ? () => response.nextPageRequest() : undefined;
            },
            error: () => {
                if (loadGeneration !== this.requestsLoadGeneration) {
                    return;
                }
                this.isRequestsLoading = false;
                this.isMoreRequestsLoading = false;
                if (append) {
                    this.requestsLoadMoreError = 'We could not load more Marketplace requests. Please try again.';
                } else {
                    this.requestsLoadError = 'We could not load the Marketplace requests. Please try again.';
                }
            }
        });
    }

    private projectDisplayName(projectId: string): string {
        return this.projects.find(project => project.id === projectId)?.name || `Project ${projectId}`;
    }

    private projectSelectionDisplayLabel(projectIds: string[]): string {
        const projectNames = projectIds.map(projectId => this.projectDisplayName(projectId));
        if (projectNames.length <= 3) {
            return projectNames.join(', ');
        }
        return `${projectNames.length} projects (${projectNames.slice(0, 3).join(', ')}, …)`;
    }

    private beginDialogSubmission(): void {
        this.interactionSubmissionPending = true;
        this.interactionError = '';
    }

    private failDialogSubmission(message: string): void {
        this.interactionSubmissionPending = false;
        this.interactionError = message;
    }

    private showDialog(dialog: MarketplaceDialog): void {
        if (!this.dialogTrigger &&
            this.document.activeElement instanceof HTMLElement &&
            this.document.activeElement !== this.document.body) {
            this.dialogTrigger = this.document.activeElement;
        }
        this.dialog = dialog;
    }

    private scrollContentToTop(): void {
        this.marketplaceContent?.nativeElement.scrollTo({top: 0});
    }
}
